import React, { useEffect, useState } from "react";
import { useCreateDepartmentUserMapping, useGetCompanies, useGetDepartments, useGetVerticals, useUpdateDepartmentUserMapping } from "../../../../backend/masters";
import { DEFAULT_OPTIONS_PAYLOAD } from "../../../common/Table";
import { toast } from "react-toastify";
import { API_RESULT, ERROR_MESSAGES, UI_DELIMITER } from "../../../../utils/constants";
import { ACTIONS } from "../../../common/Constants";
import FormRenderer, { ComponentMapper, FormTemplate, componentTypes } from "../../../common/FormRenderer";
import { validatorTypes } from "@data-driven-forms/react-form-renderer";
import { getValue, preventDefault } from "../../../../utils/common";
import { useGetUsers } from "../../../../backend/users";
import { Button, Modal } from "react-bootstrap";
import PageLoader from "../../../shared/PageLoader";


function AddEditDepartmentUser(this: any, { action, record: data, onClose, onSubmit }: any) {
    const [form, setForm] = useState<any>({});

    const [details, setDetails] = useState<any>({ hideButtons: true });

    const { companies } = useGetCompanies({ ...DEFAULT_OPTIONS_PAYLOAD, filters: { is_parent_eq: true, is_active_eq: true } });

    const [company, setCompany] = useState<any>(null);

    const { verticals } = useGetVerticals({
        ...DEFAULT_OPTIONS_PAYLOAD, filters: { company_id_eq: (company || {}).value }
    }, Boolean(company));

    const [vertical, setVertical] = useState<any>(null);

    const { departments } = useGetDepartments({
        ...DEFAULT_OPTIONS_PAYLOAD, filters: { vertical_id_eq: (vertical || {}).value }
    }, Boolean(vertical));

    const { records: users } = useGetUsers({ ...DEFAULT_OPTIONS_PAYLOAD, include: {user_roles: {include: ['role']}}, includes: ['user_roles'] });

    const { createDepartmentUserMapping, creating } = useCreateDepartmentUserMapping((response: any) => {
        if (response && response.error_messages.length > 0) {
            toast.error(response.error_messages.join(', '));
        } else {
            toast.success(`Mapping created successfully.`);
            onSubmit();
        }
    }, errorCallback);

    const { updateDepartmentUserMapping, updating } = useUpdateDepartmentUserMapping((response: any) => {
        if (response && response.error_messages.length > 0) {
            toast.error(response.error_messages.join(', '));
        } else {
            toast.success(`Mapping updated successfully.`);
            onSubmit();
        }
    }, errorCallback);

    function errorCallback(error: any) {
        const data = ((error || {}).response || {}).data || {};
        const errorMessage = data && data.error_messages && data.error_messages.length ? (data.error_messages || []).join(", ") : ERROR_MESSAGES.DEFAULT;
        toast.error(errorMessage);
    }

    const schema = {
        fields: [
            {
                component: componentTypes.SELECT,
                name: 'user',
                label: 'User',
                options: users,
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                content: '',
                formatOptionLabel: userOptionLabel
            },
            {
                component: componentTypes.SELECT,
                name: 'company',
                label: 'Company',
                options: companies,
                onChange: handleCompanyChange.bind(this),
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                content: ''
            },
            {
                component: componentTypes.SELECT,
                name: 'vertical',
                label: 'Vertical',
                options: verticals,
                onChange: handleVerticalChange.bind(this),
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                content: ''
            },
            {
                component: componentTypes.SELECT,
                name: 'department',
                label: 'Department',
                options: departments,
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                content: ''
            }
        ],
    };

    function userOptionLabel({ label, user = {} }: any) {
        const roles = (user.user_roles || []).map(({role}: any) => role.name);
        return (
            <div className="d-flex flex-column">
                <div>{label}</div>
                <div className="text-sm fw-bold fst-italic text-wrap text-black-600">Role(s): {roles.join(UI_DELIMITER)}</div>
            </div>
        )
    }

    function handleCompanyChange(event: any) {
        setCompany(event);
        setVertical(null);
        setDetails({ ...details, company: event, vertical: null, department: null });
    }

    function handleVerticalChange(event: any) {
        setVertical(event);
        setDetails({ ...details, vertical: event, department: null });
    }

    function debugForm(_form: any) {
        setForm(_form);
        setDetails(_form.values);
    }

    function submit(e: any) {
        preventDefault(e);
        if (form.valid) {
            const { user, department } = details;
            const request: any = {
                user_id: user.value,
                department_id: department.value
            };

            if (action === ACTIONS.EDIT) {
                request['id'] = data.id;
                updateDepartmentUserMapping(request);
            } else {
                createDepartmentUserMapping(request);
            }
        }
    }

    function getTitle() {
        if (action === ACTIONS.ADD) {
            return 'Add User Department Mapping';
        } else if (action === ACTIONS.EDIT) {
            return 'Edit User Department Mapping';
        }
    }

    useEffect(() => {
        if (data) {
            const { user, department } = data;
            const { vertical } = department || {};
            const { company } = vertical || {};
            setDetails({
                hideButtons: true,
                user: { value: (user || {}).id, label: (user||{}).name },
                company: { value: company.id, label: company.name },
                vertical: { value: vertical.id, label: vertical.name },
                department: { value: (department || {}).id, label: (department || {}).name }
            });
            setCompany({ value: company.id, label: company.name });
            setVertical({ value: vertical.id, label: vertical.name });
        }
    }, [data]);

    return (
        <>
            <Modal show={true} backdrop="static" dialogClassName="drawer" animation={false}>
                <Modal.Header closeButton={true} onHide={onClose}>
                    <Modal.Title className="bg">{getTitle()}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <FormRenderer FormTemplate={FormTemplate}
                        initialValues={details}
                        componentMapper={ComponentMapper}
                        schema={schema}
                        debug={debugForm}
                    />
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    {
                      <>
                          <Button variant="outline-secondary" className="btn btn-outline-secondary px-4" onClick={onClose}>{'Cancel'}</Button>
                          <Button variant="primary" onClick={submit} className="px-4" disabled={!form.valid}>{'Submit'}</Button>
                      </>
                    }
                </Modal.Footer>
            </Modal>
            {
                (creating || updating) &&
                <PageLoader>
                    {creating ? 'Creating User...' : 'Updating User...'}
                </PageLoader>
            }
        </>
    )
}

export default AddEditDepartmentUser;
